import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OutlinedInput, Typography, Tab, Tabs, Box } from '@mui/material';
import { useFormik } from 'formik';
import React, { useState, useRef } from 'react';

import './styles.css';
import ModalFeedBack from '../../components/ModalFeedBack';
import LoadingApp from '../../components/LoadingApp';
import { useAccessibility } from '../../Store/contexts/AccessibilityContext';
import { sxLabel, sxTypographyTitle, sxValidationMessage } from './sxProps';
import DefaultButton from '../../components/Accessibility/DefaultButton';
import { PessoaFisicaValidation, PessoaJuridicaValidation } from '../../interfaces/validation/IdentificacaoValidation';
import { getSegundaViaExterno } from '../../services/SegundaViaExternoService';
import { onlyNumbers } from '../../utils/OnlyNumbers';
import HCaptcha from '@hcaptcha/react-hcaptcha';

interface IdentificacaoFormProps {
  onSuccess?: (data: any) => void;
}

const IdentificacaoForm: React.FC<IdentificacaoFormProps> = ({ onSuccess }) => {
  const [showModalFeedBack, setShowModalFeedBack] = useState<boolean>(false);
  const [subTitleModal, setSubTituloModal] = useState('');
  const [modalType, setModalType] = useState<'error' | 'warning'>('error');
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaError, setCaptchaError] = useState<string>('');
  const captchaRef = useRef<HCaptcha>(null);

  const { textStyleAccessibility } = useAccessibility();
  
  const formik = useFormik({
    initialValues: {
      numeroCartao: '',
      cpf: '',
      codigoEmpresa: '',
      cnpj: '',
    },
    validationSchema: activeTab === 0 ? PessoaFisicaValidation : PessoaJuridicaValidation,
    onSubmit: async (values) => {
      if (!captchaToken) {
        setCaptchaError('Por favor, complete a verificação do captcha');
        return;
      }

      setShowLoading(true);
      
      const dadosEnvio = activeTab === 0 
        ? { numeroCartao: values.numeroCartao, cpf: values.cpf, captchaToken }
        : { codigoEmpresa: values.codigoEmpresa, cnpj: values.cnpj, captchaToken };
      
      try {
        const response = await getSegundaViaExterno(dadosEnvio);
        
        if (response?.data?.sucesso) {
          if (onSuccess) {
            const contextData = {
              mensalidades: response.data.mensalidades,
              isPessoaFisica: activeTab === 0,
              codigoEmpresa: activeTab === 1 ? values.codigoEmpresa : undefined
            };
            onSuccess(contextData);
          }
          captchaRef.current?.resetCaptcha();
          setCaptchaToken('');
        }
      } catch (error: any) {
        let errorMsg = 'Erro ao buscar boleto';
        let currentModalType: 'error' | 'warning' = 'error';

        if (error?.response?.data?.mensagem) {
          errorMsg = error.response.data.mensagem;

          const normalizedMessage = errorMsg
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();

          if (normalizedMessage.includes('nao possui boletos em aberto')) {
            currentModalType = 'warning';
          }
        } else if (error?.message) {
          errorMsg = error.message;
        }

        setModalType(currentModalType);
        setSubTituloModal(errorMsg);
        setShowModalFeedBack(true);
        captchaRef.current?.resetCaptcha();
        setCaptchaToken('');
      } finally {
        setShowLoading(false);
      }
    },
    validateOnChange: false,
  });

  const handleModalFeedBack = (): void => {
    setShowModalFeedBack((show) => !show);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    formik.setErrors({});
  };

  const onVerifyCaptcha = (token: string) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const onExpireCaptcha = () => {
    setCaptchaToken('');
    setCaptchaError('O captcha expirou. Por favor, complete novamente.');
  };

  const onErrorCaptcha = (err: string) => {
    console.error('Erro no captcha:', err);
    setCaptchaError('Erro ao carregar o captcha. Por favor, tente novamente.');
  };

  const enableButton = captchaToken && (activeTab === 0 
    ? formik.values.cpf && formik.values.numeroCartao
    : formik.values.cnpj && formik.values.codigoEmpresa);

  return (
    <div className="width-100">
      <ModalFeedBack
        showModal={showModalFeedBack}
        handleShowModal={handleModalFeedBack}
        icon={modalType}
        textButton={'Fechar'}
        title={modalType === 'warning' ? 'Aviso' : 'Erro'}
        subTitle={subTitleModal}
        showButtonModal
        handleButtonModal={handleModalFeedBack}
        classNameModal={modalType === 'warning' ? 'modal-feedback-warning' : 'modal-feedback-error'}
      />
      <LoadingApp showLoading={showLoading} />
      
      <form className="width-100 login-container-end" onSubmit={formik.handleSubmit}>
        <div style={textStyleAccessibility} className="container-login-painel-rigth">
          <Typography
            style={textStyleAccessibility}
            sx={sxTypographyTitle}
            className="margin-bottom-painel-rigth"
          >
            Documento de identificação
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1 }} className="width-100">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Tipo de pessoa"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: '#00995D',
                  height: 3,
                },
              }}
            >
              <Tab
                label="Pessoa Física"
                sx={{
                  flex: 1,
                  fontFamily: activeTab === 0 ? 'UnimedSans-bold' : 'Unimed-Sans',
                  fontWeight: activeTab === 0 ? 'bold' : 'normal',
                  color: activeTab === 0 ? '#383838' : '#8B8B8B',
                  '&.Mui-selected': {
                    color: '#333',
                    fontFamily: 'UnimedSans-bold',
                  },
                }}
              />
              <Tab
                label="Pessoa Jurídica"
                sx={{
                  flex: 1,
                  fontFamily: activeTab === 1 ? 'UnimedSans-bold' : 'Unimed-Sans',
                  fontWeight: activeTab === 1 ? 'bold' : 'normal',
                  color: activeTab === 1 ? '#383838' : '#8B8B8B',
                  '&.Mui-selected': {
                    color: '#333',
                    fontFamily: 'UnimedSans-bold',
                  },
                }}
              />
            </Tabs>
          </Box>

          <div className="width-100 display-flex flex-col gap-20">
            {/* Campos para Pessoa Física */}
            {activeTab === 0 && (
              <>
                {/* Número do Cartão */}
                <div className="width-100">
                  <label aria-label="Número do cartão do titular" htmlFor="numeroCartaoField" />
                  <Typography style={textStyleAccessibility} sx={sxLabel}>
                    Número do Cartão do Titular
                  </Typography>
                  <OutlinedInput
                    aria-describedby="errNumeroCartao"
                    id="numeroCartaoField"
                    fullWidth
                    type={'number'}
                    value={formik.values.numeroCartao}
                    placeholder="Exemplo: 99999999999999999"
                    onChange={(event) => {
                      formik.setFieldValue('numeroCartao', onlyNumbers(event.target.value));
                    }}
                    name="numeroCartao"
                    error={!!formik.errors.numeroCartao}
                    maxRows={1}
                    inputProps={{ maxLength: 30 }}
                  />
                  {formik.errors.numeroCartao && (
                    <div className="container-validation-message">
                      <FontAwesomeIcon icon={faCircleExclamation} size="sm" color="#D84141" />
                      <Typography
                        id="errNumeroCartao"
                        sx={sxValidationMessage}
                        style={textStyleAccessibility}
                      >
                        {formik.errors.numeroCartao}
                      </Typography>
                    </div>
                  )}
                </div>

                {/* CPF do Titular */}
                <div className="width-100">
                  <Typography style={textStyleAccessibility} sx={sxLabel}>
                    CPF do Titular
                  </Typography>
                  <label aria-label="CPF do titular" htmlFor="cpfField" />
                  <OutlinedInput
                    aria-describedby="errCpf"
                    fullWidth
                    type={'number'}
                    value={formik.values.cpf}
                    placeholder="Exemplo: 123.456.789-09"
                    onChange={(event) => {
                      formik.setFieldValue('cpf', onlyNumbers(event.target.value));
                    }}
                    name="cpf"
                    id="cpfField"
                    error={!!formik.errors.cpf}
                    maxRows={1}
                    inputProps={{ maxLength: 11 }}
                  />
                  {formik.errors.cpf && (
                    <div className="container-validation-message">
                      <FontAwesomeIcon icon={faCircleExclamation} size="sm" color="#D84141" />
                      <Typography id="errCpf" sx={sxValidationMessage} style={textStyleAccessibility}>
                        {formik.errors.cpf}
                      </Typography>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Campos para Pessoa Jurídica */}
            {activeTab === 1 && (
              <>
                {/* Código da Empresa */}
                <div className="width-100">
                  <label aria-label="Código da empresa" htmlFor="codigoEmpresaField" />
                  <Typography style={textStyleAccessibility} sx={sxLabel}>
                    Código da Empresa
                  </Typography>
                  <OutlinedInput
                    aria-describedby="errCodigoEmpresa"
                    id="codigoEmpresaField"
                    fullWidth
                    type={'number'}
                    value={formik.values.codigoEmpresa}
                    placeholder="Exemplo: 99999999999999999"
                    onChange={(event) => {
                      formik.setFieldValue('codigoEmpresa', onlyNumbers(event.target.value));
                    }}
                    name="codigoEmpresa"
                    error={!!formik.errors.codigoEmpresa}
                    maxRows={1}
                    inputProps={{ maxLength: 20 }}
                  />
                  {formik.errors.codigoEmpresa && (
                    <div className="container-validation-message">
                      <FontAwesomeIcon icon={faCircleExclamation} size="sm" color="#D84141" />
                      <Typography
                        id="errCodigoEmpresa"
                        sx={sxValidationMessage}
                        style={textStyleAccessibility}
                      >
                        {formik.errors.codigoEmpresa}
                      </Typography>
                    </div>
                  )}
                </div>

                {/* CNPJ */}
                <div className="width-100">
                  <Typography style={textStyleAccessibility} sx={sxLabel}>
                    CNPJ
                  </Typography>
                  <label aria-label="CNPJ" htmlFor="cnpjField" />
                  <OutlinedInput
                    aria-describedby="errCnpj"
                    fullWidth
                    type={'text'}
                    value={formik.values.cnpj}
                    placeholder="Exemplo: 12.345.678/0001-90"
                    onChange={(event) => {
                      formik.setFieldValue(
                        'cnpj',
                        event.target.value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase()
                      );
                    }}
                    name="cnpj"
                    id="cnpjField"
                    error={!!formik.errors.cnpj}
                    maxRows={1}
                    inputProps={{ maxLength: 14 }}
                  />
                  {formik.errors.cnpj && (
                    <div className="container-validation-message">
                      <FontAwesomeIcon icon={faCircleExclamation} size="sm" color="#D84141" />
                      <Typography id="errCnpj" sx={sxValidationMessage} style={textStyleAccessibility}>
                        {formik.errors.cnpj}
                      </Typography>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className='width-100 captcha-container' style={{ marginTop: '20px'}}>
              <HCaptcha 
                ref={captchaRef}
                sitekey={process.env.REACT_APP_HCAPTCHA_KEY || ''}
                onVerify={onVerifyCaptcha}
                onExpire={onExpireCaptcha}
                onError={onErrorCaptcha}
              />
              {captchaError && (
                <div className='container-validation-message'>
                  <FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
                  <Typography sx={sxValidationMessage} style={textStyleAccessibility}>
                    {captchaError}
                  </Typography>
                </div>
              )}
            </div>

            <DefaultButton
              text="GERAR 2ª VIA DO BOLETO"
              type="submit"
              disabled={!enableButton}
              style={{ marginTop: '10px' }}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default IdentificacaoForm;





